<template>
  <v-form ref="form" v-model="valid" validate-on="input lazy">
    <div class="body1 mb-6">
      {{ $t("modals.createMspBackupAccount.description") }}
    </div>
    <v-select
      v-model="localValue.item.dataCenter"
      data-testid="backup-and-restore-data-center-select"
      variant="outlined"
      :items="dataCenters"
      :item-title="dataCenterTitle"
      item-value="code"
      :loading="dataCentersLoading"
      :rules="[required()]"
      :label="$t('modals.createMspBackupAccount.dataCenter.label')"
      :placeholder="$t('modals.createMspBackupAccount.dataCenter.placeholder')"
      :no-data-text="$t('general.noData')"
      persistent-hint
      :hint="$t('modals.createMspBackupAccount.dataCenter.hint')"
    >
      <template #message="{ message }">
        <span class="d-flex align-center">
          <v-icon size="16" class="mr-1" icon="$infoXs" />
          {{ message }}
        </span>
      </template>
    </v-select>
  </v-form>
</template>

<script lang="ts">
import { defineComponent, onMounted, type PropType, type Ref, ref, watch } from "vue";
import { required } from "@/_helpers/validators.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import {
  type CreateMspBackupAccountPayload,
  type DataCenterDto,
  useMspCloudBackupStore,
} from "@/_store/msp-cloud-backup.module.ts";
import { useI18n } from "vue-i18n";

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<Partial<CreateMspBackupAccountPayload>>>,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const backupAndRestoreStore = useMspCloudBackupStore();
    const dataCenters = ref<DataCenterDto[]>([]);
    const dataCentersLoading = ref(false);
    const { t } = useI18n();
    const localValue: Ref<DialogDataConfig<Partial<CreateMspBackupAccountPayload>>> = ref({
      item: { dataCenter: "" },
      action: props.config.action,
    });
    const valid = ref(true);
    const form = ref();

    const dataCenterTitle = (dataCenter: DataCenterDto) =>
      dataCenter.country
        ? `${dataCenter.country} - ${dataCenter.city}`
        : t("modals.createMspBackupAccount.dataCenter.placeholder");

    const validate = async () => {
      const { valid } = await form.value.validate();
      return valid;
    };

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true }
    );

    watch(
      valid,
      (newVal: boolean | null) => {
        emit("update:valid", newVal ?? true);
      },
      { immediate: false }
    );

    onMounted(async () => {
      dataCentersLoading.value = true;
      try {
        dataCenters.value = await backupAndRestoreStore.getDataCenters();
      } finally {
        dataCentersLoading.value = false;
      }
    });

    return {
      localValue,
      valid,
      form,
      dataCenters,
      dataCentersLoading,
      dataCenterTitle,
      required,
      validate,
    };
  },
});
</script>

<style lang="scss" scoped></style>
